import { useNavigate, useRouterState } from "@tanstack/react-router";
import { LogOut, Menu, User } from "lucide-react";
import { signOut, useSession } from "@/auth/client";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TERRITORIES } from "@/lib/territories";

const pageNames: Record<string, string> = {
  "/consultants/summary": "Consultant Hub",
  "/consultants/territories": "All territories",
  ...Object.fromEntries(TERRITORIES.map((t) => [`/consultants/territory/${t.key}`, t.label])),
  ...Object.fromEntries(
    TERRITORIES.map((t) => [
      `/consultants/division/${t.division.toLowerCase()}`,
      `${t.division} division`,
    ]),
  ),
  "/deal-metrics": "Deal Metrics",
  "/my-dashboard": "My Dashboard",
  "/users": "User Management",
  "/settings": "Settings",
};

interface TopbarProps {
  onMobileMenuOpen: () => void;
}

export function Topbar({ onMobileMenuOpen }: TopbarProps) {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const user = session?.user;

  const loaderData = routerState.matches.at(-1)?.loaderData as Record<string, any> | undefined;

  let pageName: string;
  if (currentPath.startsWith("/owner-detail/") && loaderData?.owner) {
    pageName = `${loaderData.owner.firstName} ${loaderData.owner.lastName}`;
  } else if (currentPath.startsWith("/owner-metrics/") && loaderData?.ownerName) {
    pageName = loaderData.ownerName;
  } else {
    pageName = pageNames[currentPath] || currentPath.split("/").filter(Boolean).pop() || "Home";
  }

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  const handleLogout = async () => {
    await signOut();
    navigate({ to: "/" });
  };

  return (
    <header className="flex h-16 items-center justify-between border-b border-border/50 bg-white/80 backdrop-blur-sm px-4 lg:px-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden h-9 w-9"
          onClick={onMobileMenuOpen}
        >
          <Menu className="h-5 w-5" />
        </Button>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbPage className="text-sm font-medium">{pageName}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 rounded-full">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs bg-brand-navy text-white">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate({ to: "/settings" })}>
              <User className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
